import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getSportsAppointmentsByIdentity,
    requestSportsCancellation,
    confirmSportsCancellation
} from '../../api/sportsAppointmentService';
import { formatCurrency } from '../../utils/formatters';
import '../../styles/pages/sportsAppointmentCancellation.scss';
import { FaTimesCircle, FaCheckCircle, FaEnvelope, FaInfoCircle } from 'react-icons/fa';

const SportsAppointmentCancellation = () => {
    const navigate = useNavigate();
    const [identityNumber, setIdentityNumber] = useState('');
    const [appointments, setAppointments] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);

    // OTP Dialog State
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Lock body scroll when modal is open
    useEffect(() => {
        if (showOtpModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showOtpModal]);

    const calculateHoursUntil = (appointmentDate, startTime) => {
        const [hours, minutes] = startTime.split(':').map(Number);
        const apptDateTime = new Date(appointmentDate);
        apptDateTime.setHours(hours, minutes, 0, 0);
        const now = new Date();
        const diffMs = apptDateTime - now;
        return Math.floor(diffMs / (1000 * 60 * 60));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!identityNumber || identityNumber.length !== 11) {
            setError('Lütfen geçerli bir TC Kimlik Numarası giriniz.');
            return;
        }

        setLoading(true);
        setSearched(false);
        const result = await getSportsAppointmentsByIdentity(identityNumber);
        setLoading(false);

        if (result.isSuccess) {
            const activeAppointments = result.data.filter(apt =>
                apt.status === 'Confirmed' || apt.status === 'Pending'
            );
            setAppointments(activeAppointments);
            setSearched(true);
            if (activeAppointments.length === 0) {
                setError('Adınıza kayıtlı aktif randevu bulunamadı.');
            }
        } else {
            setError(result.resultMessage || 'Bir hata oluştu.');
        }
    };

    const handleRequestCancel = async (appointment) => {
        setSelectedAppointment(appointment);
        setSubmitting(true);
        setError('');

        const result = await requestSportsCancellation({
            appointmentId: appointment.id,
            identityNumber: identityNumber
        });
        setSubmitting(false);

        if (result.isSuccess) {
            setSuccess('Doğrulama kodu e-posta adresinize gönderildi.');
            setVerificationCode('');
            setShowOtpModal(true);
        } else {
            setError(result.resultMessage || 'İptal talebi gönderilemedi.');
        }
    };

    const handleConfirmCancel = async () => {
        if (!verificationCode || verificationCode.length < 6) {
            setError('Lütfen 6 haneli doğrulama kodunu giriniz.');
            return;
        }

        setSubmitting(true);
        setError('');

        const result = await confirmSportsCancellation({
            appointmentId: selectedAppointment.id,
            verificationCode: verificationCode
        });
        setSubmitting(false);

        if (result.isSuccess) {
            const data = result.data;

            if (data.refundProcessed) {
                setSuccess(`✓ ${data.message} İade işlemi başlatıldı.`);
            } else {
                setSuccess(`✓ ${data.message}`);
            }

            setShowOtpModal(false);
            const newList = appointments.filter(x => x.id !== selectedAppointment.id);
            setAppointments(newList);
            setSelectedAppointment(null);
            setVerificationCode('');
        } else {
            setError(result.resultMessage || 'İptal işlemi başarısız.');
        }
    };

    return (
        <div className="sports-cancellation-page">
            <div className="container">
                <header className="sc-header">
                    <h1>Spor Tesisi Randevu İptali</h1>
                    <p>Randevularınızı görüntülemek ve iptal etmek için lütfen bilgilerinizi giriniz.</p>
                </header>

                <div className="sc-refund-policy">
                    <FaInfoCircle className="sc-policy-icon" />
                    <div>
                        <strong>İade Politikası:</strong>
                        <ul>
                            <li>Randevu saatinden <strong>24 saat veya daha önce</strong> iptal edilen randevularda ücret iadesi yapılır.</li>
                            <li>24 saatten az süre kala yapılan iptallerde randevu iptal edilir ancak ücret iadesi <strong>yapılmaz</strong>.</li>
                        </ul>
                    </div>
                </div>

                {error && (
                    <div className="sc-alert sc-alert-error">
                        <FaTimesCircle /> {error}
                    </div>
                )}

                {success && (
                    <div className="sc-alert sc-alert-success">
                        <FaCheckCircle /> {success}
                    </div>
                )}

                <form onSubmit={handleSearch} className="sc-search-form">
                    <div className="sc-form-group">
                        <label htmlFor="identityNumber">TC Kimlik Numarası</label>
                        <input
                            id="identityNumber"
                            type="text"
                            value={identityNumber}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                if (val.length <= 11) setIdentityNumber(val);
                            }}
                            placeholder="TC Kimlik Numaranız"
                            maxLength={11}
                            required
                        />
                    </div>
                    <button type="submit" className="sc-btn sc-btn-primary" disabled={loading}>
                        {loading ? 'Sorgulaniyor...' : 'Randevularimi Sorgula'}
                    </button>
                </form>

                {searched && appointments.length === 0 && !error && (
                    <div className="sc-alert sc-alert-info">
                        Bu kimlik numarasına ait aktif randevu bulunamadı.
                    </div>
                )}

                <div className="sc-appointments-list">
                    {appointments.map((apt) => {
                        const hoursUntil = calculateHoursUntil(apt.appointmentDate, apt.startTime);
                        const canRefund = hoursUntil >= 24;

                        return (
                            <div key={apt.id} className="sc-appointment-card">
                                <div className="sc-card-header">
                                    <h3>{apt.facilityName}</h3>
                                </div>
                                <div className="sc-card-body">
                                    <p><strong>Tarih:</strong> {new Date(apt.appointmentDate).toLocaleDateString('tr-TR', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}</p>
                                    <p><strong>Saat:</strong> {apt.startTime} - {apt.endTime}</p>
                                    <p><strong>Tutar:</strong> <span className="sc-price">{formatCurrency(apt.paymentAmount)}</span></p>
                                    <p className="sc-email"><FaEnvelope /> {apt.email}</p>
                                </div>
                                <div className="sc-card-footer">
                                    <button
                                        className="sc-btn sc-btn-danger"
                                        onClick={() => handleRequestCancel(apt)}
                                        disabled={submitting}
                                    >
                                        <FaTimesCircle /> İptal Et
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* OTP Modal */}
            {showOtpModal && (
                <div className="sc-modal-overlay" onClick={() => setShowOtpModal(false)}>
                    <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sc-modal-header">
                            <h2>Doğrulama Kodu</h2>
                            <button className="sc-modal-close" onClick={() => setShowOtpModal(false)}>?</button>
                        </div>
                        <div className="sc-modal-body">
                            <p>
                                <strong>{selectedAppointment?.email}</strong> adresine g?nderilen 6 haneli kodu giriniz.
                            </p>
                            <input
                                type="text"
                                className="sc-otp-input"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                maxLength={6}
                                autoFocus
                            />
                        </div>
                        <div className="sc-modal-footer">
                            <button className="sc-btn sc-btn-secondary" onClick={() => setShowOtpModal(false)}>
                                Kapat
                            </button>
                            <button
                                className="sc-btn sc-btn-danger"
                                onClick={handleConfirmCancel}
                                disabled={submitting}
                            >
                                {submitting ? 'İşleniyor...' : 'Onayla ve İptal Et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SportsAppointmentCancellation;
